import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/categories");
      setCats(data);
    } catch {
      toast.error("Could not load categories");
    }
  };

  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/categories", { name: name.trim() });
      setName("");
      toast.success("Category added");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <section className="max-w-2xl" data-testid="category-manager">
      <form onSubmit={add} className="flex items-end gap-3 mb-8">
        <div className="flex-1">
          <p className="overline text-[#76746F] mb-2">Add a category</p>
          <input
            data-testid="new-category-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Travel"
            className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
          />
        </div>
        <button
          data-testid="add-category"
          type="submit"
          disabled={busy}
          className="bg-[#1A1A1A] text-white px-5 py-3 rounded-full hover:bg-[#D4A373] transition-colors text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="space-y-2">
        {cats.length === 0 && <p className="text-[#76746F] text-sm">No categories yet.</p>}
        {cats.map((c) => (
          <div
            key={c.id}
            data-testid={`category-${c.slug}`}
            className="flex items-center justify-between bg-white border border-black/10 rounded-lg px-5 py-3"
          >
            <div>
              <p className="font-serif-display text-xl">{c.name}</p>
              <p className="text-xs text-[#76746F]">/{c.slug}</p>
            </div>
            <button
              data-testid={`delete-category-${c.slug}`}
              onClick={() => remove(c.id)}
              className="w-9 h-9 rounded-full border border-black/15 hover:bg-[#B33A3A] hover:text-white hover:border-[#B33A3A] transition-colors inline-flex items-center justify-center"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
